import { useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/request-reset", { email: email.trim().toLowerCase() });
      setMessage(data.detail);
    } catch (err) {
      setError(extractApiError(err, "Request failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
      <header className="border-b border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Wordmark size="sm" testid="forgot-header-wordmark" /></Link>
          <Link to="/login" data-testid="forgot-to-login-link" className="text-sm text-fg-secondary hover:text-fg-primary transition-colors duration-200">Back to log in</Link>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-md">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary">Reset your password</h1>

          {message && (
            <Alert className="mt-6 border-progress-teal-bg bg-progress-teal-bg/40" data-testid="forgot-success">
              <AlertDescription className="text-fg-primary">{message}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive" className="mt-6" data-testid="forgot-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="forgot-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                data-testid="forgot-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-bg-surface border-border-subtle text-fg-primary"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !email}
              data-testid="forgot-submit-btn"
              className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Request reset"}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
